import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useResponsive from '../../hooks/useResponsive';
// --- SVG Icons for the Navbar ---
const UserIcon = () => <svg height="20" width="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>;
const MenuIcon = () => <svg height="24" width="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>;
const CloseIcon = () => <svg height="24" width="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;

// --- The Navbar Component ---
export default function LandingPageNavbar() {
    const { isMobile } = useResponsive();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isNavbarSolid, setIsNavbarSolid] = useState(false);
    const accessToken = useSelector((state) => state.auth?.access_token);
    const isLoggedIn = Boolean(accessToken || localStorage.getItem('access_token'));
    const ctaPath = isLoggedIn ? '/dashboard' : '/sign-in';
    const ctaLabel = isLoggedIn ? 'Dashboard' : 'Get Started';

    useEffect(() => {
        if (!isMobile) setIsMenuOpen(false);
    }, [isMobile]);

    // --- Styling Objects (CSS-in-JS) ---
    const styles = {
        nav: {
            background: '#ffffff',
            padding: '20px 32px',
            borderBottom: '1px solid #e2e8f0',
            position: 'relative',
        },
        solidNav:{
            background: '#efecec',
            padding: '20px 32px',
            borderBottom: '1px solid #e2e8f0',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
        },
        container: {
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        logo: {
            fontWeight: 'bold',
            fontSize: '32px',
            color: '#000000',
        },
        // --- Desktop Menu Styles ---
        desktopMenu: {
            display: 'flex',
            justifyContent: 'center', // Center the links
            alignItems: 'center',
            gap: '32px',
            flex: 1, // Allow this to take up the middle space
        },
        navLink: {
            color: '#4A5568',
            fontWeight: 500,
            textDecoration: 'none',
            fontSize: '16px',
        },
        desktopActions: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
        },
        loginButton: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            color: '#4A5568',
            cursor: 'pointer',
            width: '44px',
            height: '44px',
        },
        getStartedButton: {
            background: '#24a0ed', // Blue color from the image. [1]
            color: '#ffffff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontWeight: 'bold',
            cursor: 'pointer',
            fontSize: '16px',
        },
        // --- Mobile Styles ---
        hamburgerButton: {
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
        },
        mobileMenu: {
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: '#ffffff',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            padding: '20px 32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
            zIndex: 1000,
        }
    };




    useEffect(() => {

        const handleScroll = () => {
            const scrollTop = window.scrollY;
            if(scrollTop > 50) {
                setIsNavbarSolid(true);
            } else {
                setIsNavbarSolid(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);    



    } , [])

    return (
        <nav style={isNavbarSolid ? styles.solidNav : styles.nav} role="navigation" aria-label="Main navigation">
            <div style={styles.container}>
                <div style={styles.logo}><img style={{width:"50px" , height: "50px"}} src='/assets/nexorah-logo.png' alt="logo" /></div>

                {isMobile ? (
                    // --- MOBILE VIEW ---
                    <button style={styles.hamburgerButton} onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu">
                        {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
                    </button>
                ) : (
                    // --- DESKTOP VIEW ---
                    <>
                        <div style={styles.desktopMenu}>
                            <NavLink to='/' style={styles.navLink}>Home</NavLink>
                            <NavLink to={{ pathname: '/', hash: '#why-choose-heading' }} style={styles.navLink}>About</NavLink>
                                <NavLink to={{ pathname: '/', hash: '#domains-heading' }} style={styles.navLink}>Features</NavLink>
                            <NavLink to='/resources' style={styles.navLink}>Resources</NavLink>
                            <NavLink to='/learning-hub' style={styles.navLink}>Learning Hub</NavLink>
                                <NavLink to={{ pathname: '/', hash: '#pricing-heading' }} style={styles.navLink}>Pricing</NavLink>
                            <NavLink to='/contact' style={styles.navLink}>Contact</NavLink>
                        </div>
                        <div style={styles.desktopActions}>
                            <NavLink to = '/account-settings'>
                            <button style={styles.loginButton} aria-label="Login">
                                <UserIcon />
                            </button>
                            </NavLink>
                            <NavLink to={ctaPath} style={styles.navLink}>
                                <button type="button" style={styles.getStartedButton}>{ctaLabel}</button>
                            </NavLink>
                        </div>
                    </>
                )}
            </div>

            {/* --- Conditionally Rendered Mobile Menu --- */}
            {isMobile && isMenuOpen && (
                <div style={styles.mobileMenu}>
                    <NavLink to='/' style={styles.navLink} onClick={() => setIsMenuOpen(false)}>Home</NavLink>
                    <NavLink to={{ pathname: '/', hash: '#why-choose-heading' }} style={styles.navLink} onClick={() => setIsMenuOpen(false)}>About</NavLink>
                        <NavLink to={{ pathname: '/', hash: '#domains-heading' }} style={styles.navLink} onClick={() => setIsMenuOpen(false)}>Features</NavLink>
                    <NavLink to='/resources' style={styles.navLink} onClick={() => setIsMenuOpen(false)}>Resources</NavLink>
                    <NavLink to='/learning-hub' style={styles.navLink} onClick={() => setIsMenuOpen(false)}>Learning Hub</NavLink>
                        <NavLink to={{ pathname: '/', hash: '#pricing-heading' }} style={styles.navLink} onClick={() => setIsMenuOpen(false)}>Pricing</NavLink>
                    <NavLink to='/contact' style={styles.navLink} onClick={() => setIsMenuOpen(false)}>Contact</NavLink>
                    <NavLink to={isLoggedIn ? '/dashboard' : '/sign-up'} style={styles.getStartedButton} onClick={() => setIsMenuOpen(false)}>
                        {ctaLabel}
                    </NavLink>
                </div>
            )}
        </nav>
    );
}
